// frontend/src/services/TechnicalIndicatorsService.js
import * as TI from 'technicalindicators';

// Extract the indicators from the library
const { 
  SMA, 
  EMA, 
  RSI, 
  MACD, 
  BollingerBands, 
  Stochastic,
  WilliamsR,
  ATR,
} = TI;

/**
 * Technical Indicators Service
 * Handles client-side calculation of standard technical indicators
 */
const DEBUG = process.env.REACT_APP_DEBUG === 'true';

class TechnicalIndicatorsService {
  
  /**
   * Calculate Simple Moving Average
   * @param {Array} prices - Array of price values
   * @param {number} period - SMA period (default: 20)
   * @returns {Array} Array of SMA values with timestamps
   */
  static calculateSMA(candleData, period = 20) {
    if (!candleData || candleData.length < period) return [];
    
    const prices = candleData.map(candle => candle.close);
    const smaValues = SMA.calculate({ period, values: prices });
    
    // Map SMA values back to timestamps (starting from period-1 index)
    return smaValues.map((value, index) => {
      const timeIndex = index + period - 1;
      return {
        time: candleData[timeIndex]?.time || Date.now() - (smaValues.length - index) * 3600000,
        value: value || 0
      };
    });
  }

  /**
   * Calculate Exponential Moving Average
   * @param {Array} candleData - Array of candle data
   * @param {number} period - EMA period (default: 20)
   * @returns {Array} Array of EMA values with timestamps
   */
  static calculateEMA(candleData, period = 20) {
    if (!candleData || candleData.length < period) return [];
    
    const prices = candleData.map(candle => candle.close);
    const emaValues = EMA.calculate({ period, values: prices });
    
    return emaValues.map((value, index) => {
      const timeIndex = index + period - 1;
      return {
        time: candleData[timeIndex]?.time || Date.now() - (emaValues.length - index) * 3600000,
        value: value || 0
      };
    });
  }

  /**
   * Calculate RSI (Relative Strength Index)
   * @param {Array} candleData - Array of candle data
   * @param {number} period - RSI period (default: 14)
   * @returns {Array} Array of RSI values with timestamps
   */
  static calculateRSI(candleData, period = 14) {
    if (!candleData || candleData.length < period + 1) return [];
    
    const prices = candleData.map(candle => candle.close);
    const rsiValues = RSI.calculate({ period, values: prices });
    
    return rsiValues.map((value, index) => {
      const timeIndex = index + period;
      return {
        time: candleData[timeIndex]?.time || Date.now() - (rsiValues.length - index) * 3600000,
        value: value || 50
      };
    });
  }

  /**
   * Calculate MACD (Moving Average Convergence Divergence)
   * @param {Array} candleData - Array of candle data
   * @param {Object} options - MACD options {fastPeriod: 12, slowPeriod: 26, signalPeriod: 9}
   * @returns {Array} Array of MACD values with timestamps
   */
  static calculateMACD(candleData, options = {}) {
    const { fastPeriod = 12, slowPeriod = 26, signalPeriod = 9 } = options;
    
    if (!candleData || candleData.length < slowPeriod + signalPeriod) return [];
    
    const prices = candleData.map(candle => candle.close);
    const macdValues = MACD.calculate({ 
      fastPeriod, 
      slowPeriod, 
      signalPeriod, 
      values: prices,
      SimpleMAOscillator: false,
      SimpleMASignal: false
    });
    // Correct time alignment: macdValues length = candles - (slowPeriod -1) - (signalPeriod -1)
    // So first MACD point corresponds to candle index (slowPeriod + signalPeriod - 2)
    // A simpler and safer way: align to the tail so last MACD point shares the last candle's time.
    const startIndex = candleData.length - macdValues.length; // ensures last points line up
    return macdValues.map((value, index) => ({
      time: candleData[startIndex + index].time, // guaranteed within bounds
      MACD: value.MACD ?? 0,
      signal: value.signal ?? 0,
      histogram: value.histogram ?? 0
    }));
  }

  /**
   * Calculate Bollinger Bands
   * @param {Array} candleData - Array of candle data
   * @param {Object} options - Bollinger Bands options {period: 20, stdDev: 2}
   * @returns {Array} Array of Bollinger Bands values with timestamps
   */
  static calculateBollingerBands(candleData, options = {}) {
    const { period = 20, stdDev = 2 } = options;
    
    if (!candleData || candleData.length < period) return [];
    
    const prices = candleData.map(candle => candle.close);
    const bbValues = BollingerBands.calculate({ period, stdDev, values: prices });
    
    return bbValues.map((value, index) => ({
      time: candleData[index + period - 1].time,
      upper: value.upper,
      middle: value.middle,
      lower: value.lower
    }));
  }

  /**
   * Calculate Stochastic Oscillator
   * @param {Array} candleData - Array of candle data
   * @param {Object} options - Stochastic options {period: 14, signalPeriod: 3}
   * @returns {Array} Array of Stochastic values with timestamps
   */
  static calculateStochastic(candleData, options = {}) {
    const { period = 14, signalPeriod = 3 } = options;
    
    if (!candleData || candleData.length < period + signalPeriod) return [];
    
    const input = {
      high: candleData.map(candle => candle.high),
      low: candleData.map(candle => candle.low),
      close: candleData.map(candle => candle.close),
      period,
      signalPeriod
    };
    
    const stochValues = Stochastic.calculate(input);
    
    return stochValues.map((value, index) => ({
      time: candleData[index + period + signalPeriod - 1].time,
      k: value.k,
      d: value.d
    }));
  }

  /**
   * Calculate Williams %R
   * @param {Array} candleData - Array of candle data
   * @param {number} period - Williams %R period (default: 14)
   * @returns {Array} Array of Williams %R values with timestamps
   */
  static calculateWilliamsR(candleData, period = 14) {
    if (!candleData || candleData.length < period) return [];
    
    const input = {
      high: candleData.map(candle => candle.high),
      low: candleData.map(candle => candle.low),
      close: candleData.map(candle => candle.close),
      period
    };
    
    const williamsRValues = WilliamsR.calculate(input);
    
    return williamsRValues.map((value, index) => ({
      time: candleData[index + period - 1].time,
      value: value
    }));
  }

  /**
   * Calculate ATR (Average True Range)
   * @param {Array} candleData - Array of candle data
   * @param {number} period - ATR period (default: 14)
   * @returns {Array} Array of ATR values with timestamps
   */
  static calculateATR(candleData, period = 14) {
    if (!candleData || candleData.length < period + 1) return [];
    
    const input = {
      high: candleData.map(candle => candle.high),
      low: candleData.map(candle => candle.low),
      close: candleData.map(candle => candle.close),
      period
    };
    
    const atrValues = ATR.calculate(input);
    
    return atrValues.map((value, index) => {
      const timeIndex = index + period;
      return {
        time: candleData[timeIndex]?.time || Date.now() - (atrValues.length - index) * 3600000,
        value: value || 0
      };
    });
  }

  /**
   * Calculate VWAP (Volume Weighted Average Price)
   * @param {Array} candleData - Array of candle data with volume
   * @returns {Array} Array of VWAP values with timestamps
   */
  static calculateVWAP(candleData) {
    if (!candleData || candleData.length === 0) return [];
    
    const vwapData = [];
    let cumulativeVolumePrice = 0;
    let cumulativeVolume = 0;
    
    for (let i = 0; i < candleData.length; i++) {
      const candle = candleData[i];
      if (!candle || typeof candle !== 'object') continue;
      
      const high = candle.high || candle.close || 0;
      const low = candle.low || candle.close || 0;
      const close = candle.close || 0;
      const typicalPrice = (high + low + close) / 3;
      const volume = candle.volume || 1; // Default volume to 1 if not available
      
      cumulativeVolumePrice += typicalPrice * volume;
      cumulativeVolume += volume;
      
      const vwap = cumulativeVolume > 0 ? cumulativeVolumePrice / cumulativeVolume : typicalPrice;
      
      vwapData.push({
        time: candle.time || Date.now() - (candleData.length - i) * 3600000,
        value: vwap || 0
      });
    }
    
    return vwapData;
  }

  /**
   * Calculate all standard indicators at once
   * @param {Array} candleData - Array of candle data
   * @param {Object} config - Configuration for which indicators to calculate
   * @returns {Object} Object containing all calculated indicators
   */
  static calculateAllIndicators(candleData, config = {}) {
    const {
      sma = { enabled: true, periods: [20, 50, 200] },
      ema = { enabled: true, periods: [12, 26] },
      rsi = { enabled: true, period: 14 },
      vwap = { enabled: true },
      macd = { enabled: true, fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 },
      bollingerBands = { enabled: true, period: 30, stdDev: 2 },
      stochastic = { enabled: false, period: 14, signalPeriod: 3 },
      williamsR = { enabled: false, period: 14 },
      atr = { enabled: false, period: 14 }
    } = config;

    const indicators = {};

    try {
      // Calculate SMAs
      if (sma.enabled && sma.periods) {
        indicators.sma = {};
        sma.periods.forEach(period => {
          indicators.sma[`sma${period}`] = this.calculateSMA(candleData, period);
        });
      }

      // Calculate EMAs
      if (ema.enabled && ema.periods) {
        indicators.ema = {};
        ema.periods.forEach(period => {
          indicators.ema[`ema${period}`] = this.calculateEMA(candleData, period);
        });
      }

      // Calculate RSI
      if (rsi.enabled) {
        indicators.rsi = this.calculateRSI(candleData, rsi.period);
      }

      // Calculate VWAP
      if (vwap.enabled) {
        indicators.vwap = this.calculateVWAP(candleData);
      }

      // Calculate MACD
      if (macd.enabled) {
        indicators.macd = this.calculateMACD(candleData, {
          fastPeriod: macd.fastPeriod,
          slowPeriod: macd.slowPeriod,
          signalPeriod: macd.signalPeriod
        });
      }

      // Calculate Bollinger Bands
      if (bollingerBands.enabled) {
        indicators.bollingerBands = this.calculateBollingerBands(candleData, {
          period: bollingerBands.period,
          stdDev: bollingerBands.stdDev
        });
      }

      // Calculate Stochastic
      if (stochastic.enabled) {
        indicators.stochastic = this.calculateStochastic(candleData, {
          period: stochastic.period,
          signalPeriod: stochastic.signalPeriod
        });
      }

      // Calculate Williams %R
      if (williamsR.enabled) {
        indicators.williamsR = this.calculateWilliamsR(candleData, williamsR.period);
      }

      // Calculate ATR
      if (atr.enabled) {
        indicators.atr = this.calculateATR(candleData, atr.period);
      }

      if (DEBUG) {
        console.log('Technical indicators calculated successfully:', Object.keys(indicators));
      }
      
    } catch (error) {
      console.error('Error calculating technical indicators:', error);
    }

    return indicators;
  }

  /**
   * Get indicator configuration presets
   * @returns {Object} Object containing preset configurations
   */
  static getPresets() {
    return {
      basic: {
        sma: { enabled: false, periods: [20, 50, 200] },
        rsi: { enabled: false, period: 14 }, // Disabled by default, use separate RSI chart
        vwap: { enabled: false }, // Disabled by default
        bollingerBands: { enabled: true, period: 30, stdDev: 2 }
      },
      complete: {
        sma: { enabled: true, periods: [20, 50, 200] },
        ema: { enabled: true, periods: [12, 26] },
        rsi: { enabled: false, period: 14 }, // Disabled by default, use separate RSI chart
        vwap: { enabled: false }, // Disabled by default
        macd: { enabled: true, fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 },
        bollingerBands: { enabled: true, period: 30, stdDev: 2 }
      },
      advanced: {
        sma: { enabled: false, periods: [10, 20, 50, 200] },
        ema: { enabled: false, periods: [12, 26, 50] },
        rsi: { enabled: false, period: 14 }, // Disabled by default, use separate RSI chart
        vwap: { enabled: false }, // Disabled by default
        macd: { enabled: false, fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 },
        bollingerBands: { enabled: true, period: 30, stdDev: 2 }
      }
    };
  }
}

export default TechnicalIndicatorsService;
